import React from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import type { Campaign, CampaignStatus } from '../../types/campaign';

export interface CampaignMobileCardProps {
  /** Campaign data */
  campaign: Campaign;
  /** Whether the card is selected (for bulk operations) */
  isSelected?: boolean;
  /** Callback when checkbox is toggled */
  onToggleSelect?: (campaignId: number) => void;
  /** Callback when card is clicked */
  onClick?: (campaign: Campaign) => void;
  /** Render function for actions dropdown */
  renderActions?: (campaign: Campaign) => React.ReactNode;
}

const statusVariantMap: Record<CampaignStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
};

/**
 * Custom mobile card for campaign list
 * - Polished UX for high-traffic campaign management
 * - Checkbox for bulk operations
 * - Status badge with color coding
 * - Horizontal scrollable tags
 * - Truncated URLs
 * - Manager and date info
 * - Actions at bottom
 *
 * @example
 * ```tsx
 * <CampaignMobileCard
 *   campaign={campaign}
 *   isSelected={selectedIds.includes(campaign.id)}
 *   onToggleSelect={handleToggle}
 *   onClick={handleView}
 *   renderActions={(c) => <Dropdown items={actions} />}
 * />
 * ```
 */
export function CampaignMobileCard({
  campaign,
  isSelected = false,
  onToggleSelect,
  onClick,
  renderActions,
}: CampaignMobileCardProps) {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggleSelect?.(campaign.id);
  };

  const handleCardClick = () => {
    onClick?.(campaign);
  };

  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4',
        'transition-colors relative',
        onClick && 'cursor-pointer active:bg-gray-50'
      )}
      onClick={handleCardClick}
    >
      {/* Header with name and checkbox */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {campaign.name}
          </h3>
        </div>
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="ml-3 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      {/* Status and Tags row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Badge variant={statusVariantMap[campaign.status]}>
          {campaign.status}
        </Badge>
        {campaign.tags && campaign.tags.length > 0 && (
          <div className="flex gap-1 overflow-x-auto scrollbar-thin">
            {campaign.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
                style={{
                  backgroundColor: tag.color + '20',
                  color: tag.color,
                }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* URLs */}
      <div className="space-y-2 mb-3">
        <div>
          <span className="text-xs font-medium text-gray-500">Human URL</span>
          <p className="text-sm text-gray-900 truncate">{campaign.human_redirect_url}</p>
        </div>
        <div>
          <span className="text-xs font-medium text-gray-500">Bot URL</span>
          <p className="text-sm text-gray-900 truncate">{campaign.bot_redirect_url}</p>
        </div>
      </div>

      {/* Manager and date */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
        <div>
          {campaign.assigned_manager ? (
            <span>Manager: {campaign.assigned_manager.name}</span>
          ) : (
            <span>No manager assigned</span>
          )}
        </div>
        <div>
          {new Date(campaign.created_at).toLocaleDateString()}
        </div>
      </div>

      {/* Actions at bottom */}
      {renderActions && (
        <div className="pt-3 border-t border-gray-200">
          {renderActions(campaign)}
        </div>
      )}
    </div>
  );
}

CampaignMobileCard.displayName = 'CampaignMobileCard';
